import React from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { FiBriefcase, FiMapPin, FiClock, FiEye, FiHeart, FiTrash2 } from "react-icons/fi";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import "./EmployeeDashboard.css";

const SavedJobs = () => {
  const { savedJobs = [], setSavedJobs } = useOutletContext();
  const navigate = useNavigate();

  const toggleSave = async (jobId) => {
    try {
      const token = localStorage.getItem("candidateToken");
      if (!token) {
        navigate("/candidate-login");
        return;
      }
      const res = await axios.post(`${API_BASE_URL}/candidate/saved-jobs/toggle`, { jobId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const updatedRes = await axios.get(`${API_BASE_URL}/candidate/saved-jobs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (updatedRes.data.success) {
          setSavedJobs(updatedRes.data.savedJobs);
        }
      }
    } catch (err) {
      console.error("Toggle save error:", err);
    }
  };

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Saved <span>Jobs</span>
            </h1>
            <p className="emp-dash__subtitle">
              Your curated opportunities
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiHeart />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Table Section */}
        <div className="emp-dash__card py-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiBriefcase className="text-pink-600" /> Saved Jobs
              </h3>
              <p className="emp-dash__card-desc">Showing {savedJobs.length} saved jobs</p>
            </div>
          </div>
          {savedJobs.length > 0 ? (
            <div className="emp-dash__table-wrap overflow-x-auto !block">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-center font-bold">Job Position</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Department</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Location</th>
                    <th className="py-3 px-4 text-center font-bold">Experience</th>
                    <th className="py-3 px-4 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {savedJobs.map((job) => (
                    <tr key={job._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 text-center">
                        <div className="text-xs font-bold text-gray-800">{job.role}</div>
                        {job.jobType && (
                          <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">{job.jobType}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center hidden md:table-cell">
                        <span className="text-[10px] font-medium text-pink-700 bg-pink-50 px-2 py-0.5 rounded uppercase tracking-wider">
                          {job.department || "General Ops"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center hidden md:table-cell">
                        <div className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                          <FiMapPin className="text-pink-400" size={10} />
                          {job.location}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center align-middle">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100 shadow-sm">
                          <FiClock className="text-amber-500" size={11} />
                          {job.experience || "Fresher"}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                          <button
                            onClick={() => toggleSave(job._id)}
                            className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest rounded-lg border bg-red-50 text-red-600 border-red-100 hover:bg-red-100 transition-all"
                          >
                            <div className="flex items-center gap-1.5">
                              <FiHeart />
                              Saved
                            </div>
                          </button>
                          <button
                            onClick={() => navigate(`/jobs/${job._id}`)}
                            className="px-3 py-1 bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest rounded-lg hover:bg-blue-700 transition-all flex items-center gap-1.5 shadow-sm"
                          >
                            <FiEye size={10} /> View and Apply
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-300">
                  <FiHeart size={32} />
                </div>
                <span className="text-sm font-medium text-gray-500">No saved jobs yet</span>
                <button
                  onClick={() => navigate("/all-jobs")}
                  className="mt-2 px-6 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-md hover:opacity-90 transition"
                >
                  Explore Jobs
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiHeart className="text-pink-400" />
          Timely Health Recruitment Tracker • {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default SavedJobs;